/**
 * Capacity Calculator
 *
 * Handles capacity analysis for bond distribution strategies.
 * Extracted from UnlimitedCapacityStrategy for reusability.
 */

import { BUFFER_PERCENTAGES, INTEREST_RATES } from '~/config/financial-constants';
import type { RevenueDistributionHelperMap } from '~/engine/processing/engines/distribution-engine/types';
import type { RevenueMap } from '~/types';

/**
 * Calculates minimum revenue capacities for bonds across all future years
 */
export class CapacityCalculator {
  /**
   * Calculates the minimum revenue capacities for each bond based on the revenue map.
   * This determines the bottleneck capacity for each bond across all future years.
   * @param helperMap - Read-only helper map for bond configuration
   * @param revenueMap - Read-only revenue map with capacity data
   * @param currentTaxYearInProcess - Tax year being processed
   * @returns Map of bondId to minimum revenue capacity
   * @pure Level 5 calculator - pure calculation, no side effects
   */
  static findMinimumRevenueCapacities(
    helperMap: RevenueDistributionHelperMap,
    revenueMap: RevenueMap,
    currentTaxYearInProcess: number
  ): Map<string, number> {
    // Start with current values from helperMap
    const capacities = new Map<string, number>();
    for (const [bondId, entry] of Object.entries(helperMap)) {
      capacities.set(bondId, entry.minimumRevenueCapacity);
    }

    // Find minimum capacity for each bond across all applicable years
    for (const revenueMapEntry of Object.values(revenueMap)) {
      if (revenueMapEntry.taxYear <= currentTaxYearInProcess) {
        for (const [bondIdKey, helperMapEntry] of Object.entries(helperMap)) {
          const bondDetail = revenueMapEntry.bondSeriesDetailsByBondId[bondIdKey];

          if (bondDetail?.isActive) {
            const currentMin = capacities.get(bondIdKey) ?? helperMapEntry.minimumRevenueCapacity;
            if (revenueMapEntry.revenueCapacity < currentMin) {
              capacities.set(
                bondIdKey,
                revenueMapEntry.revenueCapacity < 0 ? 0 : revenueMapEntry.revenueCapacity
              );
            }
          }
        }
      }
    }

    return capacities;
  }

  /**
   * Applies calculated minimum capacities to the helper map.
   * @param helperMap - Helper map to update
   * @param capacities - Calculated capacities from findMinimumRevenueCapacities
   */
  static applyMinimumCapacities(
    helperMap: RevenueDistributionHelperMap,
    capacities: Map<string, number>
  ): void {
    for (const [bondId, capacity] of capacities) {
      if (helperMap[bondId]) {
        helperMap[bondId].minimumRevenueCapacity = capacity;
      }
    }
  }

  /**
   * Calculates forward DRF capacity - minimum revenue available looking forward
   * Used for conservative capacity estimation in MixedTargeting strategy
   */
  static calculateForwardDrfCapacity(
    revenueMap: RevenueMap,
    currentTaxYear: number,
    bufferPercentage: number = BUFFER_PERCENTAGES.CAPACITY_BUFFER_PERCENTAGE
  ): number {
    const futureYears = Object.values(revenueMap)
      .filter((entry) => entry.taxYear >= currentTaxYear)
      .sort((a, b) => a.taxYear - b.taxYear);

    if (futureYears.length === 0) return 0;

    const minCapacity = Math.min(...futureYears.map((entry) => entry.revenueCapacity));
    return Math.max(0, minCapacity * (1 - bufferPercentage));
  }

  /**
   * Calculates backward interest capacity - maximum principal considering future interest
   * Used for sophisticated capacity constraints in targeting strategies
   */
  static calculateBackwardInterestCapacity(
    helperMap: RevenueDistributionHelperMap,
    maxInterestRate: number = INTEREST_RATES.MAX_INTEREST_RATE_ASSUMPTION
  ): Record<string, number> {
    const capacities: Record<string, number> = {};

    Object.entries(helperMap).forEach(([bondId, entry]) => {
      if (entry.isActive) {
        // Simplified calculation - can be enhanced with actual interest schedules
        const estimatedCapacity = entry.minimumRevenueCapacity / (1 + maxInterestRate);
        capacities[bondId] = Math.max(0, estimatedCapacity);
      }
    });

    return capacities;
  }
}
